import {
  Box,
  Switch,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { useDataProvider, useNotify } from 'react-admin';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { httpClient } from 'providers/clients/httpClient';
import {
  useCallback,
  useMemo,
  useState,
  memo,
  useRef,
  useEffect,
  Fragment,
  MouseEvent,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { Editor } from '@tiptap/react';

import { cleanupAssetFields } from 'providers/contexts/extensions/functions/formatters';
import EditorWithBar from 'components/EditorWithBar/EditorWithBar';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import {
  capitalizeFirstLetter,
  getRcsTitle,
  getUntitledAssetString,
} from 'utility/functions';
import { createUncontrolledEditor } from 'components/RichTextEditor/hooks/useCreateEditor';
import { UrlInputDialog } from 'screens/editorial/asset/components/common/FileFieldInput/utility/UrlInputDialog';
import { assetActionButtonSx } from 'screens/editorial/asset/components/common/FileFieldInput/utility/FileFieldInputStyles';
import { FullEditorialAsset, RelatedContentItem } from 'utility/types/dataProvider';

import InlineEditButtons from './InlineEditButtons';
import { useInlineAsset, OverrideValues } from '../context';
import { IconHelper } from 'components/generic/IconHelper';
import { InlineRelatedContentSelect } from './InlineRelatedContentSelect';
import { t } from 'i18next';
import { useUrlToFile } from 'screens/editorial/asset/components/common/FileFieldInput/hooks/useUrlToFile';
import { v4 as uuidv4 } from 'uuid';
import InlineAssetLink from '../containers/InlineAssetLink';
import { inlineBylineToolbarButtons } from 'components/EditorBar/const';

/**
 * Extracted as a stable component to prevent unmount/remount when parent
 * re-renders (e.g. during concurrent uploads). Defining this inline caused
 * React to treat each render's function as a new component type, destroying
 * the DOM input element while the native file dialog was open.
 */
const InlineFileDropzone = memo(function InlineFileDropzone({
  allowedMimeTypes = [],
  createFileAsset,
  notify,
}: {
  allowedMimeTypes?: string[];
  createFileAsset: (file: File) => Promise<void>;
  notify: (
    message: string,
    options?: { type: 'warning' | 'error' | 'info' | 'success' },
  ) => void;
}) {
  const theme = useTheme();
  const acceptMap = useMemo(
    () => Object.fromEntries((allowedMimeTypes ?? []).map((mime: string) => [mime, []])),
    [allowedMimeTypes],
  );
  const handleDrop = useCallback(
    (uploadedFiles: File[]) => {
      if (createFileAsset && uploadedFiles[0]) {
        createFileAsset(uploadedFiles[0]);
      }
    },
    [createFileAsset],
  );
  const handleDropRejected = useCallback(
    (fileRejections: FileRejection[], event: DropEvent) => {
      // Check if more than one file was dropped
      if (fileRejections.length > 1) {
        notify(t('blox.error.only_one_file'), { type: 'warning' });
        return;
      }
      // Check if the file type is invalid
      const rejection = fileRejections[0];
      if (rejection.errors.some((error) => error.code === 'file-invalid-type')) {
        console.log('[handleDropRejected] file type not accepted:', rejection.file.type);
        notify(t('blox.error.type_not_accepted'), {
          type: 'warning',
        });
        return;
      }
    },
    [notify],
  );
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: acceptMap,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    multiple: false,
  });
  // Destructure dropzone props outside JSX for render stability
  // (avoids creating new merged object references on every render)
  const {
    onClick: _dzRootClick,
    onMouseDown: _dzRootMouseDown,
    ...restRootProps
  } = getRootProps();
  const inputProps = getInputProps();
  // Custom handlers to prevent editor selection/focus issues within tiptap
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      open();
    },
    [open],
  );
  const handleMouseDown = useCallback((e: MouseEvent) => {
    e.preventDefault();
  }, []);
  const rootSx = useMemo(
    () => ({
      border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
      borderRadius: 1,
      p: 2,
      textAlign: 'center' as const,
      cursor: 'pointer',
      width: '100%',
      bgcolor: isDragActive ? 'action.hover' : 'background.paper',
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
      },
    }),
    [isDragActive, theme.palette.primary.main, theme.palette.divider],
  );
  return (
    <Box
      {...restRootProps}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      role="button"
      tabIndex={0}
      className="blox-inline__file-input"
      sx={rootSx}
    >
      <input {...inputProps} />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {isDragActive
          ? t('blox.general.drop_file_here', 'Drop file here')
          : t(
              'blox.general.drop_or_click',
              'Drop a file to upload, or click to select it',
            )}
      </Typography>
    </Box>
  );
});

/**
 * Maps a field name to its corresponding override key in node.attrs.
 * Handles 'summary' field as an alias for 'content'.
 */
const getOverrideKey = (fieldName: string): keyof OverrideValues | null => {
  const fieldToOverrideMap: Record<string, keyof OverrideValues> = {
    title: 'override-title',
    byline: 'override-byline',
    content: 'override-content',
    summary: 'override-content', // summary maps to override-content
  };
  return fieldToOverrideMap[fieldName] ?? null;
};

/**
 * Checks if a field supports override functionality.
 */
const isOverridableField = (fieldName: string): boolean => {
  return getOverrideKey(fieldName) !== null;
};

const updateNestedState = (state: any, path: string, value: any): any => {
  const keys = path.split('.');
  const lastKey = keys.pop();

  let nestedState = { ...state };

  let nestedObject = nestedState;
  for (const key of keys) {
    if (!nestedObject[key]) {
      nestedObject[key] = {};
    }
    nestedObject = nestedObject[key];
  }

  if (lastKey) {
    nestedObject[lastKey] = value;
  }

  return nestedState;
};

const InlineAssetEdit = memo(() => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const theme = useTheme();
  const {
    inlineProps,
    inlineRecord,
    setView,
    assetConfig,
    createFileAsset,
    setInlineRecord,
    isRelatedContent,
    nodeID,
    relContentStyles,
    setIsFetchingURL,
    overrideValues,
    updateOverrideValue,
  } = useInlineAsset();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState<{ [key: string]: any }>(() => ({}));
  const editorsRef = useRef<{ [key: string]: Editor }>({});
  const formDataInitialized = useRef(false);
  const { setValue, getValues } = useFormContext();
  const [readOnly, setReadOnly] = useState(false);
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false);
  const hasInitReadOnly = useRef(false);
  const [isValidForm, setIsValidForm] = useState(false);

  // Track which fields have override enabled (derived from whether override value exists)
  const [overrideToggles, setOverrideToggles] = useState<Record<string, boolean>>(() => {
    const initialToggles: Record<string, boolean> = {};
    const overridableFields = ['title', 'byline', 'content', 'summary'];
    overridableFields.forEach((field) => {
      const overrideKey = getOverrideKey(field);
      if (overrideKey) {
        const hasOverrideValue = Boolean(overrideValues[overrideKey]);
        initialToggles[field] = hasOverrideValue;
      }
    });
    return initialToggles;
  });

  // Session-local override values - preserved during edit session until save/cancel
  // This allows users to toggle overrides on/off without losing their entered values
  const [sessionOverrideValues, setSessionOverrideValues] = useState<
    Partial<OverrideValues>
  >(() => ({ ...overrideValues }));

  // Ref to store original RichText values (for restoring when override is toggled off)
  const originalRichTextValues = useRef<Record<string, string>>({});

  // Ref to store initial override state for detecting changes
  const initialOverrideState = useRef<{
    toggles: Record<string, boolean>;
    values: OverrideValues;
  } | null>(null);

  // Initialize initial override state on first render
  if (initialOverrideState.current === null) {
    initialOverrideState.current = {
      toggles: { ...overrideToggles },
      values: { ...overrideValues },
    };
  }

  const notify = useNotify();

  // Ref to track current override toggle state for use in editor listeners
  const overrideTogglesRef = useRef<Record<string, boolean>>(overrideToggles);
  useEffect(() => {
    overrideTogglesRef.current = overrideToggles;
  }, [overrideToggles]);

  const uuid = inlineRecord?.uuid || inlineRecord?.id;

  const typeLabel = isRelatedContent
    ? t('blox.editorial.asset.related_content.label')
    : 'Asset';

  const [editorTitle, setEditorTitle] = useState<string | undefined>(
    inlineRecord?.title ||
      (inlineRecord?.type === 'relcontent'
        ? t('blox.editorial.asset.related_content.label')
        : inlineRecord?.type || ''),
  );

  const [inlineAssetType, setInlineAssetType] = useState<string>(
    inlineRecord?.type === 'relcontent'
      ? t('blox.editorial.asset.related_content.label')
      : inlineRecord?.type || '',
  );
  const editorDescription = uuid
    ? `Edit ${capitalizeFirstLetter(inlineAssetType)} ${typeLabel}`
    : t('blox.editorial.inline.placeholder', 'Placeholder');

  // Define a type for the fields to include urlAllowedFileExtensionRegExp as optional
  type InlineAssetField = {
    type: string;
    field?: string;
    label?: string;
    value?: any;
    required?: boolean;
    multiline?: boolean;
    allowedMimeTypes?: string[];
    urlAllowedFileExtensionRegExp?: RegExp;
  };

  // Initialize fields
  const [fields, setFields] = useState<InlineAssetField[]>(() => {
    const initialFields = [
      ...(assetConfig?.edit ? assetConfig.edit(inlineRecord) : []),
      //Always include draft and no_publish fields for edit
      ...(uuid && !isRelatedContent
        ? [
            {
              type: 'Checkbox',
              field: 'draft',
              label: 'blox.editorial.asset.draft.label',
              value: inlineRecord?.draft ?? true,
              required: false,
            },
            {
              type: 'Checkbox',
              field: 'properties.no_publish',
              label: 'blox.editorial.asset.properties.no_publish.label',
              value: Boolean(inlineRecord?.properties?.no_publish),
              required: false,
            },
          ]
        : []),
    ];
    return initialFields;
  });

  // Check if all required fields are populated
  useEffect(() => {
    // Helper function to check if a field value is empty
    const isEmptyValue = (value: any) => {
      if (!value || value.trim() === '') return true; // Empty string check
      if (value.trim() === '<p></p>' || value.trim() === '<p><br></p>') return true; // Empty HTML tags check
      return false; // Otherwise, it's valid
    };

    // Check if all required fields have valid values in formData
    const allRequiredFieldsFilled = fields
      .filter((field: any) => field.required) // Get only required fields
      .every((field: any) => !isEmptyValue(formData[field.field]));

    setIsValidForm(allRequiredFieldsFilled);
  }, [fields, formData]);

  useEffect(() => {
    if (inlineRecord?.force_title) {
      setReadOnly(true);
    } else if (!hasInitReadOnly.current && relContentStyles.length > 0) {
      // Minor workaround to get the related content style based on the name and default_title
      // TNCMS-555719 will remove the need for this approach
      if (inlineProps.node.attrs.type === 'relcontent') {
        const relatedContentStyle = relContentStyles.find(
          (style) =>
            style.name === inlineRecord?.style &&
            style.default_title === inlineRecord?.title,
        );

        if (relatedContentStyle) {
          setReadOnly(relatedContentStyle.force_title || false);
        }

        hasInitReadOnly.current = true; // Executed initial readonly state check
      }
    } else {
      setReadOnly(false);
    }
  }, [setReadOnly, inlineProps, relContentStyles, inlineRecord]);

  // Ensure fields update when assetConfig changes
  useEffect(() => {
    setFields((prevFields: any) => {
      // Prevent resetting fields if they have already been modified
      return prevFields.length > 0
        ? prevFields
        : assetConfig?.edit
          ? assetConfig.edit(inlineRecord)
          : [];
    });
  }, [assetConfig, inlineRecord]);

  // Handle related content style select
  const handleRelatedStyleSelect = (item: any) => {
    setEditorTitle(item.name);
    setInlineAssetType(item.type);

    const isStyleChangedFromPrevious = item.type !== formData.type;

    // Update the form data with the selected style.
    setFormData((prevState) => ({
      ...prevState,
      content: isStyleChangedFromPrevious ? null : prevState?.content || '',
      style: item.name,
      type: item.type,
      title: getRcsTitle(item),
      force_title: item.force_title || false,
    }));

    setReadOnly(item.force_title || false);

    // Update the fields with the new values.
    setFields((prevFields: any) => {
      return prevFields.map((field: any) => {
        // Update the content field with the correct new value.
        if (field.field === 'title') {
          return {
            ...field,
            value: getRcsTitle(item),
            force_title: item.force_title || false,
          };
        }

        // Update the content field with the correct new value.
        if (isStyleChangedFromPrevious && field.field === 'content') {
          return {
            ...field,
            value: null,
          };
        }

        // Update the title field with the correct new value.
        return field;
      });
    });

    // Update the editor content if the style is has been changed.
    editorsRef.current['content']?.commands.setContent(
      isStyleChangedFromPrevious ? '' : formData.content || '',
    );
  };

  const handleFieldChange = useCallback(
    (field: string, newValue: any) => {
      setFields((prevFields: any) =>
        prevFields.map((f: any) => (f.field === field ? { ...f, value: newValue } : f)),
      );

      setFormData((prevState) => ({
        ...prevState,
        [field]: newValue,
      }));
    },
    [setFields, setFormData],
  );

  /**
   * Handles toggling the override state for a field.
   * When toggled ON, the field will use the override value from session storage.
   * When toggled OFF, the field will use the original value.
   * Override values are preserved in session storage until save/cancel.
   */
  const handleOverrideToggle = useCallback(
    (field: string, checked: boolean) => {
      setOverrideToggles((prev) => ({ ...prev, [field]: checked }));

      const overrideKey = getOverrideKey(field);
      if (!overrideKey) return;

      const editor = editorsRef.current[field];

      if (checked) {
        // When toggling ON, store original value and load override value from session
        if (editor) {
          // Store current editor content as original value (only if not already stored)
          if (!originalRichTextValues.current[field]) {
            const currentContent = editor.getHTML();
            originalRichTextValues.current[field] = currentContent;
          }

          // Check session storage for existing override value
          const sessionOverrideContent = sessionOverrideValues[overrideKey];
          if (sessionOverrideContent) {
            // Restore previously entered override value from session
            editor.commands.setContent(sessionOverrideContent);
          } else {
            // Initialize session override with original value
            const originalContent = originalRichTextValues.current[field];
            setSessionOverrideValues((prev) => ({
              ...prev,
              [overrideKey]: originalContent,
            }));
          }
        } else {
          // For non-RichText fields (Text fields)
          if (!sessionOverrideValues[overrideKey]) {
            const currentValue = fields.find((f: any) => f.field === field)?.value || '';
            setSessionOverrideValues((prev) => ({
              ...prev,
              [overrideKey]: currentValue,
            }));
          }
        }
      } else {
        // When toggling OFF, save current override value to session and restore original
        if (editor) {
          // Preserve current override content in session before switching
          const currentOverrideContent = editor.getHTML();
          setSessionOverrideValues((prev) => ({
            ...prev,
            [overrideKey]: currentOverrideContent,
          }));

          // Restore original value to editor
          const originalContent =
            originalRichTextValues.current[field] ||
            fields.find((f: any) => f.field === field)?.value ||
            '';
          editor.commands.setContent(originalContent);
        } else {
          // For non-RichText fields - value is already in sessionOverrideValues via handleOverrideValueChange
        }
        // Note: We do NOT call updateOverrideValue here - values stay in session until save/cancel
      }
    },
    [fields, sessionOverrideValues],
  );

  /**
   * Handles changes to the override value for a field.
   * Updates the override value in session storage (committed on save).
   */
  const handleOverrideValueChange = useCallback((field: string, newValue: string) => {
    const overrideKey = getOverrideKey(field);
    if (overrideKey) {
      setSessionOverrideValues((prev) => ({ ...prev, [overrideKey]: newValue }));
    }
  }, []);

  /**
   * Gets the current value to display in the field based on override toggle state.
   * Uses session storage for override values.
   */
  const getFieldValue = useCallback(
    (field: string, originalValue: string): string => {
      const overrideKey = getOverrideKey(field);
      const isOverrideEnabled = overrideToggles[field];

      if (isOverrideEnabled && overrideKey && sessionOverrideValues[overrideKey]) {
        return sessionOverrideValues[overrideKey]!;
      }
      return originalValue || '';
    },
    [overrideToggles, sessionOverrideValues],
  );

  /**
   * Checks if any override has been added, updated, or removed since the component mounted.
   * Compares session state with initial state.
   * Returns true if overrides have changed, false otherwise.
   *
   * Note: Only checks fields that exist in the current form to avoid
   * issues where multiple field names map to the same override key.
   */
  const hasOverrideChanges = useCallback((): boolean => {
    if (!initialOverrideState.current) return false;

    const { toggles: initialToggles, values: initialValues } =
      initialOverrideState.current;

    // Get the actual field names present in the current form
    const formFieldNames = fields.map((f: any) => f.field);

    // Track processed override keys to avoid checking the same key twice
    const processedKeys = new Set<string>();

    for (const field of formFieldNames) {
      if (!isOverridableField(field)) continue;

      const overrideKey = getOverrideKey(field);
      if (!overrideKey || processedKeys.has(overrideKey)) continue;

      processedKeys.add(overrideKey);
      const wasEnabled = initialToggles[field] ?? false;
      const isEnabled = overrideToggles[field] ?? false;

      // Check if toggle state changed
      if (wasEnabled !== isEnabled) {
        return true;
      }

      // If override was and still is enabled, check if value changed
      if (wasEnabled && isEnabled) {
        const initialValue = initialValues[overrideKey] ?? '';
        const currentValue = sessionOverrideValues[overrideKey] ?? '';
        if (initialValue !== currentValue) {
          return true;
        }
      }
    }

    return false;
  }, [fields, overrideToggles, sessionOverrideValues]);

  /**
   * Commits session override values to context.
   * Called when user saves/updates the asset.
   * - If toggle is ON, commit the session value to context
   * - If toggle is OFF, remove the override from context
   *
   * Note: Only processes fields that exist in the current form to avoid
   * issues where multiple field names map to the same override key
   * (e.g., 'content' and 'summary' both map to 'override-content').
   */
  const commitOverrideValues = useCallback(() => {
    // Get the actual field names present in the current form
    const formFieldNames = fields.map((f: any) => f.field);

    // Track processed override keys to avoid processing the same key twice
    // (e.g., 'content' and 'summary' both map to 'override-content')
    const processedKeys = new Set<string>();

    formFieldNames.forEach((field: string) => {
      if (!isOverridableField(field)) return;

      const overrideKey = getOverrideKey(field);
      if (!overrideKey || processedKeys.has(overrideKey)) return;

      processedKeys.add(overrideKey);
      const isEnabled = overrideToggles[field] ?? false;

      if (isEnabled) {
        // Override is enabled - commit session value to context
        const sessionValue = sessionOverrideValues[overrideKey];
        if (sessionValue !== undefined) {
          updateOverrideValue(overrideKey, sessionValue);
        }
      } else {
        // Override is disabled - remove from context
        updateOverrideValue(overrideKey, undefined);
      }
    });
  }, [fields, overrideToggles, sessionOverrideValues, updateOverrideValue]);

  const onSaveClick = useCallback(
    async (event?: MouseEvent<HTMLButtonElement>) => {
      if (event) {
        event.preventDefault();
      }

      const nestedFormData = Object.keys(formData).reduce((acc, key) => {
        if (key.includes('.')) {
          return updateNestedState(acc, key, formData[key]);
        }
        return { ...acc, [key]: formData[key] };
      }, {});

      const prevAssetData = inlineRecord;
      const newAssetData = { ...inlineRecord, ...nestedFormData };

      // Handle relcontent
      if (isRelatedContent) {
        // get the current related_content from the form context
        const currentRelContent = getValues('related_content') || [];

        // Update the title and content of the matching item
        let matchFound = false;
        const updatedRelContent = currentRelContent.map((item: any) => {
          if (item.id === (newAssetData as RelatedContentItem).id) {
            // Update title and content if found
            matchFound = true;
            return {
              ...item,
              ...newAssetData,
            };
          }
          return item; // Keep the other items unchanged
        });

        // If no match was found, add the new item with id: undefined
        if (!matchFound) {
          const newItemUUID = uuidv4();

          updatedRelContent.push({
            ...newAssetData,
            id: newItemUUID,
            inline: true,
            environment: 'all',
          });

          inlineProps.updateAttributes({
            ...inlineProps.node.attrs,
            uuid: newItemUUID,
            placeholder: false,
          });
          setInlineRecord({
            ...newAssetData,
            id: newItemUUID,
          });
        } else {
          setInlineRecord(newAssetData);
        }

        // Update the form context with the updated related_content array
        setValue('related_content', updatedRelContent, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });

        // Commit session override values to context before closing
        commitOverrideValues();

        // Notify user if override changes need to be saved with the article
        if (hasOverrideChanges()) {
          notify(
            t(
              'blox.editorial.inline.override_save_reminder',
              'Article-specific overrides have been modified. Save the article to apply these changes.',
            ),
            { type: 'info', autoHideDuration: 8000 },
          );
        }

        // Close the edit view
        setView('display');
        return;
      }

      try {
        let response;

        if (prevAssetData && uuid) {
          // Format patch data for update
          const patchData = Object.keys(formData)
            .filter((key) => {
              // Exclude 'type' field
              if (key === 'type') return false;

              // Exclude overridden fields - their values are stored in node.attrs, not the original asset
              if (!isRelatedContent && isOverridableField(key) && overrideToggles[key]) {
                return false;
              }

              return true;
            })
            .map((key) => {
              let keyPath = key.replace(/\./g, '/'); // Convert dot notation to slash notation
              if (formData[key] === prevAssetData[key]) {
                return undefined; // Skip unchanged fields
              }
              if (keyPath === 'properties/no_publish') {
                return { path: `/published`, op: 'replace', value: !formData[key] };
              }
              const value =
                key === 'byline'
                  ? cleanupAssetFields({ byline: formData[key] }).byline
                  : formData[key];
              return { path: `/${keyPath}`, op: 'replace', value };
            });

          const patchOps = patchData.filter((patch) => patch !== undefined);

          if (patchOps.length === 0) {
            // No asset changes to save, but commit override values
            commitOverrideValues();

            // Notify if override changes need to be saved
            if (hasOverrideChanges()) {
              notify(
                t(
                  'blox.editorial.inline.override_save_reminder',
                  'Article-specific overrides have been modified. Save the article to apply these changes.',
                ),
                { type: 'info' },
              );
            }
            setView('display');
            return;
          }

          const patchResp = await dataProvider.patch('editorial', {
            id: uuid,
            data: patchOps,
          });

          // Extract the actual asset from the nested response structure
          // patchResp.data contains { status, data: actualAsset, id }
          response = {
            status: patchResp?.status,
            data: patchResp?.data?.data,
            id: patchResp?.id,
          };
        } else {
          const payload = {
            data: newAssetData,
          };

          ({ data: response } = await dataProvider.create('editorial', payload));
        }

        inlineProps.updateAttributes({
          uuid: response?.data?.uuid,
          type: 'asset',
          app: response?.data?.app || 'editorial',
          placeholder: false,
        });

        if (setInlineRecord) {
          setInlineRecord(response?.data);
        }

        // Commit session override values to context before closing
        commitOverrideValues();

        // Notify user if override changes need to be saved with the article
        if (hasOverrideChanges()) {
          notify(
            t(
              'blox.editorial.inline.override_save_reminder',
              'Article-specific overrides have been modified. Save the article to apply these changes.',
            ),
            { type: 'info' },
          );
        }

        setView('display');
      } catch (error) {
        console.error('[InlineAssetEdit] - handleSave - Error:', error);
      }
    },
    [
      inlineProps,
      dataProvider,
      setView,
      formData,
      getValues,
      setValue,
      isRelatedContent,
      inlineRecord,
      setInlineRecord,
      uuid,
      overrideToggles,
      hasOverrideChanges,
      commitOverrideValues,
      notify,
    ],
  );

  useEffect(() => {
    // if (fetchingData) return;
    // Listen to updates in editors
    const updateHandlers: { editor: Editor; handler: () => void }[] = [];

    Object.keys(editorsRef.current).forEach((field) => {
      const editor = editorsRef.current[field];
      if (editor) {
        const handler = () => {
          const content = editor.getHTML();
          const isOverrideEnabled = overrideTogglesRef.current[field] ?? false;
          const overrideKey = getOverrideKey(field);

          if (
            !isRelatedContent &&
            isOverridableField(field) &&
            isOverrideEnabled &&
            overrideKey
          ) {
            // Update override value in session storage (committed on save)
            setSessionOverrideValues((prev) => ({ ...prev, [overrideKey]: content }));
          } else {
            // Update original value in formData
            setFormData((prevState) => ({ ...prevState, [field]: content }));
          }
        };

        editor.on('update', handler);
        updateHandlers.push({ editor, handler });
      }
    });

    // Only initialize `formData` once
    if (!formDataInitialized.current) {
      formDataInitialized.current = true; // Mark as initialized

      const initialData: { [key: string]: any } = {};
      fields.forEach((input: any) => {
        initialData[input.field] = input.value;
      });

      setFormData((prevState) => ({ ...prevState, ...initialData }));
    }

    // Cleanup: remove listeners when effect re-runs or component unmounts
    return () => {
      updateHandlers.forEach(({ editor, handler }) => {
        editor.off('update', handler);
      });
    };
  }, [editorsRef, fields, isRelatedContent]);

  // Function to handle remote video URL submission
  const handleRemoteVideoSubmit = useCallback(
    async (url: string) => {
      let response;
      try {
        setView('display');
        inlineProps.updateAttributes({
          placeholder: false,
        });
        setIsFetchingURL(true);
        const { data } = await httpClient({
          url: `/editorial/video/remote/?url=${encodeURIComponent(url)}`,
          method: 'GET',
        });

        if (data.status === 'success') {
          type RaCreatePayload = { data: FullEditorialAsset };
          let payload: RaCreatePayload = {
            data: {
              title:
                data?.data?.asset_data?.title || getUntitledAssetString(t, 'Video', ''),
              type: 'video', // Make sure to set the type to video
              content: data?.data?.asset_data?.content,
              remote_video: data?.data?.remote_video,
              remote_video_player: data?.data?.remote_video_player,
            },
          };

          setInlineRecord((prev) => ({
            ...prev,
            title: payload.data.title,
          }));

          if (data?.data?.poster?.id) {
            payload.data = {
              ...payload.data,
              files: {
                image: {
                  add_binary: data?.data?.poster?.id,
                },
              },
            };
          }

          if (data?.data?.preview?.id) {
            payload.data = {
              ...payload.data,
              files: {
                ...(payload.data.files || {}),
                preview: {
                  add_binary: data?.data?.preview?.id,
                },
              },
            };
          }

          // Create the asset directly using dataProvider
          ({ data: response } = await dataProvider.create('editorial', payload));

          // Update the node attributes with the new asset data
          inlineProps.updateAttributes({
            uuid: response?.data?.uuid,
            type: 'asset',
            app: response?.data?.app || 'editorial',
          });

          setInlineRecord(response?.data);
          setIsFetchingURL(false);
          // Switch to display view after successful creation
          setView('display');
        }
      } catch (error) {
        console.error('[handleRemoteVideoSubmit] error:', error);
      } finally {
        setIsUrlDialogOpen(false); // Close the dialog after submission
      }
    },
    [dataProvider, inlineProps, setView, setInlineRecord, setIsFetchingURL],
  );

  // Function to handle URL submission
  const handleUrlSubmit = useUrlToFile({
    allowedRegex:
      typeof fields[0]?.urlAllowedFileExtensionRegExp === 'object'
        ? fields[0]?.urlAllowedFileExtensionRegExp
        : undefined,
    onFileReady: (file: File) => {
      createFileAsset(file);
    },
  });

  if (assetConfig === undefined || !fields) return null;

  return (
    <Fragment>
      <Box sx={{ position: 'absolute', right: '1.5rem', top: '1rem' }}>
        {!uuid ? (
          <Tooltip
            title={`${t('blox.actions.delete')} ${t('blox.editorial.inline.placeholder', 'Placeholder')}`}
            data-testid="inline-asset-edit-tooltip"
          >
            <IconButton
              aria-label="close"
              onClick={() => {
                inlineProps.deleteNode();
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <InlineAssetLink />
        )}
      </Box>
      <Box
        data-testid="inline-asset-edit"
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gridTemplateRows: 'auto',
          alignItems: 'center',
          gap: 2.5,
          '& .MuiFormHelperText-root': {
            p: 0,
            py: 1,
            px: 1.5,
            m: 0,
            textTransform: 'none',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
          },
        })}
      >
        <IconHelper iconType={inlineRecord?.type || inlineProps.node.attrs.type} />
        <Box data-testid="inline-asset-edit--form-wrapper">
          <Box
            data-testid="inline-asset-edit-title"
            contentEditable={false}
            sx={{
              fontSize: '1rem',
            }}
          >
            {capitalizeFirstLetter(editorTitle)} {uuid ? '' : 'Insert'}
          </Box>
          <Box
            data-testid="inline-asset-edit-description"
            contentEditable={false}
            sx={{
              fontSize: '0.75rem',
              fontWeight: '300',
              gridColumn: '1 / span 2',
            }}
          >
            {editorDescription}
          </Box>
        </Box>
        <Box sx={{ gridColumn: '1 / span 2' }}>
          <Box
            data-testid="inline-asset-edit-form"
            id={`${nodeID}-edit-form`}
            sx={{
              display: 'grid',

              gridTemplateColumns: {
                md: isRelatedContent ? '1fr 2fr' : '1fr', // Mobile/Tablet
              },

              // Adds spacing between elements
              gap: 2,

              // Vertically center items in each row
              alignItems: 'center',

              '& > :nth-of-type(n+3)':
                isRelatedContent && !isMobileOrTablet ? { gridColumn: 'span 2' } : {},
            }}
          >
            {fields.map((input: any, index: number) => {
              const {
                type,
                field,
                label,
                value,
                required,
                multiline,
                showCount,
                allowedMimeTypes,
                urlAllowedFileExtensionRegExp,
              } = input;
              const isForcedTitle = field === 'title' && (input.force_title || readOnly);

              switch (type) {
                case 'Select': {
                  if (isRelatedContent) {
                    return (
                      <Box
                        key={index}
                        sx={{ gridColumn: '1 / -1' }}
                      >
                        <InlineRelatedContentSelect onSelect={handleRelatedStyleSelect} />
                      </Box>
                    );
                  }
                  return null; // Add standard select if/when needed
                }
                case 'Text': {
                  const canOverride = !isRelatedContent && isOverridableField(field);
                  const isOverrideEnabled = overrideToggles[field] ?? false;
                  const displayValue = canOverride
                    ? getFieldValue(
                        field,
                        fields.find((f: any) => f.field === field)?.value || '',
                      )
                    : fields.find((f: any) => f.field === field)?.value || '';

                  const handleTextChange = (newValue: string) => {
                    if (canOverride && isOverrideEnabled) {
                      handleOverrideValueChange(field, newValue);
                    } else {
                      handleFieldChange(field, newValue);
                    }
                  };

                  const fieldId = `inline-asset-edit-textfield--${field}`;

                  return (
                    <FormControl
                      key={index}
                      fullWidth
                      variant="outlined"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <InputLabel
                          htmlFor={fieldId}
                          sx={{
                            position: 'relative',
                            transform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'text.primary',
                          }}
                        >
                          {t(label)}
                          {isForcedTitle &&
                            ` (${t('blox.editorial.inline.read_only', 'Read only')})`}
                          {required && ' *'}
                        </InputLabel>
                        {canOverride && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: isOverrideEnabled
                                  ? 'primary.main'
                                  : 'text.secondary',
                                fontWeight: isOverrideEnabled ? 500 : 400,
                              }}
                            >
                              {isOverrideEnabled
                                ? t(
                                    'blox.editorial.inline.article_specific',
                                    'Article-specific',
                                  )
                                : t('blox.editorial.inline.asset_value', 'Asset value')}
                            </Typography>
                            <Switch
                              data-testid={`inline-asset-override-toggle--${field}`}
                              checked={isOverrideEnabled}
                              onChange={(e) =>
                                handleOverrideToggle(field, e.target.checked)
                              }
                              size="small"
                              slotProps={{
                                input: {
                                  'aria-label': `Toggle override for ${field}`,
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      <TextField
                        id={fieldId}
                        fullWidth
                        data-testid={fieldId}
                        name={field}
                        value={displayValue}
                        onChange={(event) => handleTextChange(event.target.value)}
                        variant="outlined"
                        multiline={multiline}
                        rows={multiline ? 4 : 1}
                        sx={{
                          bgcolor: 'background.paper',
                        }}
                        slotProps={{
                          input: {
                            readOnly: isForcedTitle,
                            endAdornment: !isForcedTitle && (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="clear input"
                                  onClick={() => handleTextChange('')}
                                  edge="end"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </FormControl>
                  );
                }
                case 'RichText': {
                  // note: name empty string to avoid registering rich text
                  // to react hook form and not push collab extensions
                  // as name is used for the ydoc field fragment so
                  // if name is passed to EditorWithBar/RichTextInput
                  // react hook form controller will register it causing
                  // issues with dirty state diffing when editing asset inline
                  // eg if you console log rhf dirty fields you may see
                  // { ..otherFields, bloxInline_66-content: true}

                  const canOverride = !isRelatedContent && isOverridableField(field);
                  const isOverrideEnabled = overrideToggles[field] ?? false;
                  const overrideKey = getOverrideKey(field);

                  // Determine initial content for editor
                  const initialContent =
                    canOverride && isOverrideEnabled && overrideKey
                      ? overrideValues[overrideKey] || value
                      : value;

                  if (!editorsRef.current[field]) {
                    // Store original value in ref for later restoration
                    if (canOverride) {
                      originalRichTextValues.current[field] = value || '';
                    }

                    /** 20260825 - CMSADMIN-1563 In order to have an inline editor
                     * for the byline respect the byline extensions rules, field name
                     * is passed and then the collab feature checks the type to determine if it loads
                     */
                    const editorInstance = createUncontrolledEditor({
                      editorConfig: {
                        contentForEditor: initialContent,
                        name: field,
                        type: 'inline',
                        placeholder: t(label),
                        disableDropCursor: true,
                      },
                    });
                    editorsRef.current[field] = editorInstance;
                  }

                  const fieldId = `inline-asset-edit-richtext--${field}`;

                  return (
                    <Box
                      key={index}
                      data-testid={fieldId}
                    >
                      <FormControl
                        fullWidth
                        sx={{ gridColumn: '1 / -1' }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <InputLabel
                            htmlFor={fieldId}
                            shrink
                            sx={{
                              position: 'relative',
                              transform: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: 'text.primary',
                            }}
                          >
                            {t(label)}
                          </InputLabel>
                          {canOverride && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isOverrideEnabled
                                    ? 'primary.main'
                                    : 'text.secondary',
                                  fontWeight: isOverrideEnabled ? 500 : 400,
                                }}
                              >
                                {isOverrideEnabled
                                  ? t(
                                      'blox.editorial.inline.article_specific',
                                      'Article-specific',
                                    )
                                  : t('blox.editorial.inline.asset_value', 'Asset value')}
                              </Typography>
                              <Switch
                                data-testid={`inline-asset-override-toggle--${field}`}
                                checked={isOverrideEnabled}
                                onChange={(e) =>
                                  handleOverrideToggle(field, e.target.checked)
                                }
                                size="small"
                                slotProps={{
                                  input: {
                                    'aria-label': `Toggle override for ${field}`,
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </FormControl>
                      <EditorWithBar
                        editor={editorsRef.current[field]}
                        name="" // dont register to rhf
                        type="richtext"
                        showCount={showCount}
                        toolbarButtons={
                          field === 'byline' ? inlineBylineToolbarButtons : undefined
                        }
                        autoComplete="off"
                      />
                    </Box>
                  );
                }
                case 'File': {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        gridColumn: '1 / -1',
                      }}
                    >
                      <InlineFileDropzone
                        allowedMimeTypes={allowedMimeTypes}
                        createFileAsset={createFileAsset}
                        notify={notify}
                      />
                      {(urlAllowedFileExtensionRegExp ||
                        inlineProps.HTMLAttributes.type === 'video') && (
                        <>
                          <Button
                            onClick={() => setIsUrlDialogOpen(true)}
                            variant="text"
                            color="primary"
                            size="small"
                            sx={assetActionButtonSx}
                            endIcon={<LinkIcon color={'inherit'} />}
                          >
                            {t('blox.general.use_url')}
                          </Button>
                          <UrlInputDialog
                            open={isUrlDialogOpen}
                            onClose={() => setIsUrlDialogOpen(false)}
                            onSubmitUrl={
                              inlineProps.HTMLAttributes.type === 'video'
                                ? handleRemoteVideoSubmit
                                : handleUrlSubmit
                            }
                            customValidation={
                              urlAllowedFileExtensionRegExp
                                ? urlAllowedFileExtensionRegExp?.test.bind(
                                    urlAllowedFileExtensionRegExp,
                                  )
                                : undefined
                            }
                          />
                        </>
                      )}
                    </Box>
                  );
                }
                case 'Hidden': {
                  return (
                    <input
                      key={index}
                      type="hidden"
                      name={field}
                      value={value}
                    />
                  );
                }
                case 'Checkbox': {
                  return (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={
                            fields.find((f: any) => f.field === field)?.value || false
                          } // Use latest state
                          onChange={(event) =>
                            handleFieldChange(field, event.target.checked)
                          }
                          data-testid={`related-asset-edit-checkbox--${field}`}
                        />
                      }
                      label={t(label)}
                      data-testid={`related-asset-edit-checkbox-label--${field}`}
                    />
                  );
                }
                default: {
                  return null;
                }
              }
            })}
            <InlineEditButtons
              handleSaveClick={onSaveClick}
              validForm={isValidForm}
            />
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
});

export default InlineAssetEdit;
